import {
  InvalidUser,
  UserImportErrorTypeUser
} from '@dadohr/shared/domainTypes/user';
import { useMemo } from 'react';
import { useSort } from '../../hooks/useSort';
import { useUnsafeTranslation } from '../../i18next';
import { FlexContainer } from '../../layout/Flex';
import { useCurrentUser } from '../../services/currentUser';
import { toFullName } from '../../services/user';
import { AlertCircleRed } from '../AlertCircleRed';
import {
  ItemSorter,
  ItemSorters,
  RowsRenderer,
  ROW_HEIGHTS
} from '../GroupableList';
import { IColumn } from '../Table/VirtualizedSortable';

type D = InvalidUser;
type O = { emailDomain: string };
type Column = IColumn<D, 'icon' | 'name' | 'email', O>;

const ROW_TO_KEY = (d: D) => d.user.id;

const EmailCell = ({ d, o }: { d: D; o: O }) => {
  const { t } = useUnsafeTranslation();
  if (d.issue === UserImportErrorTypeUser.NO_WORK_EMAIL) {
    return <strong>{t('Missing')}</strong>;
  }
  if (d.issue === UserImportErrorTypeUser.INVALID_EMAIL_DOMAIN) {
    return (
      <strong>
        {t(`Email doesn't match @{{ domain }} domain`, {
          domain: o.emailDomain
        })}
      </strong>
    );
  }
  return <div>{d.user.data.email}</div>;
};

const NameCell = ({ d }: { d: D }) => {
  const { t } = useUnsafeTranslation();
  if (d.issue === UserImportErrorTypeUser.NO_FIRST_NAME) {
    return <strong>{t('Missing first name')}</strong>;
  }
  if (d.issue === UserImportErrorTypeUser.NO_LAST_NAME) {
    return <strong>{t(`Missing last name`)}</strong>;
  }
  return <div>{toFullName(d.user.data)}</div>;
};

const COLUMNS: Column[] = [
  {
    key: 'icon',
    head: () => '',
    cell: () => (
      <FlexContainer>
        <AlertCircleRed size={20} />
      </FlexContainer>
    ),
    align: 'left',
    sortable: false,
    width: 40,
    flexGrow: 0
  },
  {
    key: 'name',
    head: () => 'Name',
    cell: (d, o) => <NameCell d={d} />,
    align: 'left',
    sortable: false,
    width: 200,
    flexGrow: 0
  },
  {
    key: 'email',
    head: () => 'Work Email',
    cell: (d, o) => <EmailCell d={d} o={o} />,
    align: 'left',
    sortable: false,
    width: 200,
    flexGrow: 1
  }
];

const SORTERS: ItemSorters<D, O> = {
  asTheyAppearInTheCSV: {
    key: 'asTheyAppearInTheCSV',
    items: {
      sort: (d) => 0,
      dir: 'asc'
    }
  }
};

const getHostName = (url: string) => {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
};

export const CSVImportIssuesTable = ({
  ds,
  variant = 'contained'
}: {
  ds: InvalidUser[];
  variant?: 'contained' | 'plain';
}) => {
  const { org } = useCurrentUser();
  const emailDomain = getHostName(org.domain);
  const otherProps = useMemo(() => ({ emailDomain }), [emailDomain]);

  const { sortBy, direction, setSort } = useSort<ItemSorter<D, O>>(
    SORTERS.asTheyAppearInTheCSV,
    SORTERS.asTheyAppearInTheCSV.items.dir
  );
  return (
    <RowsRenderer
      variant={variant}
      columns={COLUMNS}
      rows={ds}
      rowToKey={ROW_TO_KEY}
      sorter={sortBy}
      sortDirection={direction}
      renderHead={true}
      headProps={{ sticky: true }}
      onHeadClick={(c, dir) => {
        const sorter = SORTERS[c.key];
        if (!sorter) {
          return;
        }
        setSort(sorter, dir || sorter.items.dir);
      }}
      chunkSize={30}
      rootMargin="300px"
      rowHeight={ROW_HEIGHTS.dense}
      otherProps={otherProps}
    />
  );
};
